// MainLayoutBase.tsx

import { Box } from '@mui/material';
import { useCommonLoggedInData } from 'CommonLoggedInData';
import Sidebar from 'Sidebar';
import React from 'react';
import Breadcrumbs from './Breadcrumbs';
import { FloatingActionStackProvider } from './FloatingActionStack';
import Header from './Header';
import VeraFloatingChat from './VeraFloatingChat';
import { Palette } from 'Palette';
import { CONTENT_TOP_PADDING_EXTRA } from 'DesignTokens';
import { useFidesChat } from '../context/FidesChatContext';

// Keep these in sync with Header's container
const CONTENT_MAX_WIDTH = 1264; // <- match Header's container max width
const CONTENT_GUTTER_X = { xs: 0, md: '2rem' }; // <- match Header's horizontal padding

const SIDEBAR_COLLAPSED_WIDTH = 64;
const SIDEBAR_EXPANDED_WIDTH = 240;
const CHAT_PANEL_WIDTH = 420;

interface MainLayoutProps {
  children: React.ReactNode;
  appendSection?: () => React.ReactNode;
  minimal?: boolean;
  // Investor-only: the global Vera floating chat calls POST /vera/global-chat,
  // a route the backend only mounts when ADMIN_MODE is off. Keep it out of the
  // Admin app, where that request 404s.
  showVeraChat?: boolean;
  avatarBgColor?: string;
}

export default function MainLayoutBase({
  children,
  appendSection,
  minimal,
  showVeraChat = false,
  avatarBgColor,
}: MainLayoutProps) {
  const context = useCommonLoggedInData();
  const chat = useFidesChat();

  const sidebarWidth = context?.sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  const chatOpen = Boolean(chat?.isPanelOpen && !chat?.isChatMinimized);

  return (
    <FloatingActionStackProvider>
      <Box
        sx={{
          backgroundColor: Palette.body.background,
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        className="main-layout"
      >
        {/* Fixed elements */}
        <Header avatarBgColor={avatarBgColor} />
        {!minimal && <Sidebar />}

        {/* Main row: sidebar spacer + scrollable content column + chat spacer */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flex: 1,
            position: 'relative',
            zIndex: 0, // Ensure it's below the fixed sidebar (sidebar z-index is ~1199)
          }}
          className="main-layout-content"
        >
          {/* Sidebar spacer so content never slides under sidebar (md+) */}
          {!minimal && (
            <Box
              sx={{
                width: { xs: 0, md: `${sidebarWidth}px` },
                flexShrink: 0,
                transition: 'width 300ms ease-in-out',
                display: { xs: 'none', md: 'block' },
                pointerEvents: 'none', // Allow clicks to pass through to the fixed sidebar behind
              }}
              className="main-layout-sidebar"
            />
          )}

          {/* Main column (fills remaining space) */}
          <Box sx={{ flex: 1, minWidth: 0 }} className="main-layout-main-wrapper">
            {/* Centered container that matches Header's content width & padding */}
            <Box
              component="main"
              sx={{
                // push under header, plus extra breathing room (admin only)
                mt: {
                  xs: `calc(72px + ${CONTENT_TOP_PADDING_EXTRA})`,
                  md: `calc(80px + ${CONTENT_TOP_PADDING_EXTRA})`,
                },
                pb: '3.75rem',
                px: CONTENT_GUTTER_X,
                width: '100%',
                maxWidth: CONTENT_MAX_WIDTH,
                mx: 'auto', // centers within the remaining space to the right of the sidebar
                boxSizing: 'border-box',
              }}
              className="main-layout-main-content"
            >
              <Breadcrumbs noSidePaddingFrom="lg" />
              {children}
            </Box>
          </Box>

          {/* Chat panel spacer — reserves room for the fixed chat drawer so content doesn't go underneath */}
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: 0, sm: chatOpen ? `${CHAT_PANEL_WIDTH}px` : 0 },
              transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
              pointerEvents: 'none',
              display: { xs: 'none', sm: 'block' },
            }}
            className="main-layout-chat-spacer"
          />
        </Box>

        {appendSection && appendSection()}

        {/* Global Vera floating chat — Investor UI only (see showVeraChat) */}
        {showVeraChat && <VeraFloatingChat />}
      </Box>
    </FloatingActionStackProvider>
  );
}
